// src/features/invoice/components/InvoiceList.jsx
import React, { useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Typography,
  Box,
  Tooltip,
  Menu,
  MenuItem,
  LinearProgress,
  Chip,
} from "@mui/material";
import {
  Visibility,
  Edit,
  Delete,
  Send,
  MoreVert,
  CheckCircle,
  Cancel,
  Pending,
  Payment,
  Print,
} from "@mui/icons-material";
import {
  formatCurrency,
  getStatusLabel,
  getStatusColor,
} from "../utils/invoiceHelpers";
import InvoiceStatusBadge from "./InvoiceStatusBadge";

const InvoiceList = ({
  invoices = [],
  loading = false,
  onEdit,
  onDelete,
  onStatusChange,
  onSend,
  onPreview,
  onRecordPayment,
  onPrint,
}) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedInvoice, setSelectedInvoice] = useState(null);

  const handleMenuOpen = (event, invoice) => {
    setAnchorEl(event.currentTarget);
    setSelectedInvoice(invoice);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedInvoice(null);
  };

  const handleAction = (action) => {
    if (!selectedInvoice) return;

    switch (action) {
      case "view":
        onPreview && onPreview(selectedInvoice);
        break;
      case "edit":
        onEdit && onEdit(selectedInvoice);
        break;
      case "delete":
        onDelete && onDelete(selectedInvoice.id);
        break;
      case "send":
        onSend && onSend(selectedInvoice.id);
        break;
      case "payment":
        onRecordPayment && onRecordPayment(selectedInvoice.id);
        break;
      case "print":
        onPrint && onPrint(selectedInvoice);
        break;
      default:
        break;
    }
    handleMenuClose();
  };

  const canEdit = (status) => {
    return ["draft"].includes(status);
  };

  const canSend = (status) => {
    return ["draft"].includes(status);
  };

  const canRecordPayment = (status) => {
    return ["sent", "viewed", "partially_paid", "overdue"].includes(status);
  };

  const getActions = (invoice) => {
    const actions = [];

    actions.push({
      key: "view",
      label: "View",
      icon: <Visibility fontSize="small" />,
    });

    if (canEdit(invoice.status)) {
      actions.push({
        key: "edit",
        label: "Edit",
        icon: <Edit fontSize="small" />,
      });
    }

    // if (canSend(invoice.status)) {
    //   actions.push({
    //     key: "send",
    //     label: "Send to Client",
    //     icon: <Send fontSize="small" />,
    //   });
    // }

    // if (
    //   canRecordPayment(invoice.status) &&
    //   invoice.total > (invoice.amountPaid || 0)
    // ) {
    //   actions.push({
    //     key: "payment",
    //     label: "Record Payment",
    //     icon: <Payment fontSize="small" />,
    //   });
    // }

    actions.push({
      key: "print",
      label: "Print",
      icon: <Print fontSize="small" />,
    });
    actions.push({
      key: "delete",
      label: "Delete",
      icon: <Delete fontSize="small" />,
      danger: true,
    });

    return actions;
  };

  const getBalanceDue = (invoice) => {
    return (invoice.total || 0) - (invoice.amountPaid || 0);
  };

  // ✅ Force Indian Rupee symbol
  const formatIndianCurrency = (amount) => {
    if (amount === undefined || amount === null) return "₹0.00";
    const num = Number(amount);
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(num);
  };

  if (loading) {
    return <LinearProgress sx={{ mt: 2 }} />;
  }

  if (!invoices || invoices.length === 0) {
    return (
      <Paper sx={{ p: 4, textAlign: "center" }}>
        <Typography variant="h6" color="text.secondary">
          No invoices found
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Create your first invoice to get started
        </Typography>
      </Paper>
    );
  }

  return (
    <TableContainer component={Paper}>
      <Table>
        <TableHead>
          <TableRow sx={{ bgcolor: "#f5f5f5" }}>
            <TableCell>Invoice #</TableCell>
            <TableCell>Title</TableCell>
            <TableCell>Client</TableCell>
            <TableCell>Date</TableCell>
            <TableCell>Due Date</TableCell>
            <TableCell align="right">Amount (₹)</TableCell>
            <TableCell>Status</TableCell>
            <TableCell align="center">Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {invoices.map((invoice) => (
            <TableRow key={invoice.id} hover>
              <TableCell>
                <Typography variant="body2" fontWeight="bold">
                  {invoice.number}
                </Typography>
              </TableCell>
              <TableCell>
                <Typography variant="body2" noWrap sx={{ maxWidth: 150 }}>
                  {invoice.title || "Untitled"}
                </Typography>
              </TableCell>
              <TableCell>{invoice.clientName}</TableCell>
              <TableCell>
                {new Date(invoice.date).toLocaleDateString("en-IN")}
              </TableCell>
              <TableCell>
                <Typography
                  color={
                    new Date(invoice.dueDate) < new Date() &&
                    invoice.status !== "paid"
                      ? "error"
                      : "text.primary"
                  }
                >
                  {new Date(invoice.dueDate).toLocaleDateString("en-IN")}
                </Typography>
              </TableCell>
              <TableCell align="right">
                <Typography variant="body2" fontWeight="bold" color="primary">
                  {formatIndianCurrency(invoice.total)}
                </Typography>
                {invoice.amountPaid > 0 && (
                  <Typography
                    variant="caption"
                    color="success.main"
                    display="block"
                  >
                    Paid: {formatIndianCurrency(invoice.amountPaid)}
                  </Typography>
                )}
                {getBalanceDue(invoice) > 0 && invoice.status !== "paid" && (
                  <Typography variant="caption" color="error" display="block">
                    Due: {formatIndianCurrency(getBalanceDue(invoice))}
                  </Typography>
                )}
              </TableCell>
              <TableCell>
                <InvoiceStatusBadge status={invoice.status} />
                {invoice.isOverdue && invoice.status !== "paid" && (
                  <Chip
                    label="OVERDUE"
                    color="error"
                    size="small"
                    sx={{ ml: 1, height: 20, fontSize: "10px" }}
                  />
                )}
              </TableCell>
              <TableCell align="center">
                <Box
                  sx={{ display: "flex", justifyContent: "center", gap: 0.5 }}
                >
                  <Tooltip title="View">
                    <IconButton
                      size="small"
                      onClick={() => onPreview && onPreview(invoice)}
                    >
                      <Visibility fontSize="small" />
                    </IconButton>
                  </Tooltip>

                  {canEdit(invoice.status) && (
                    <Tooltip title="Edit">
                      <IconButton
                        size="small"
                        onClick={() => onEdit && onEdit(invoice)}
                      >
                        <Edit fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}

                  {/* {canSend(invoice.status) && (
                    <Tooltip title="Send">
                      <IconButton
                        size="small"
                        onClick={() => onSend && onSend(invoice.id)}
                        color="primary"
                      >
                        <Send fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )} */}

                  {canRecordPayment(invoice.status) &&
                    getBalanceDue(invoice) > 0 && (
                      <Tooltip title="Record Payment">
                        <IconButton
                          size="small"
                          onClick={() =>
                            onRecordPayment && onRecordPayment(invoice.id)
                          }
                          color="success"
                        >
                          <Payment fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}

                  <Tooltip title="More">
                    <IconButton
                      size="small"
                      onClick={(e) => handleMenuOpen(e, invoice)}
                    >
                      <MoreVert fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      {/* Action Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        {selectedInvoice &&
          getActions(selectedInvoice).map((action) => (
            <MenuItem
              key={action.key}
              onClick={() => handleAction(action.key)}
              sx={{
                color: action.danger ? "error.main" : "inherit",
              }}
            >
              {action.icon}
              <Typography variant="body2" sx={{ ml: 1 }}>
                {action.label}
              </Typography>
            </MenuItem>
          ))}
      </Menu>
    </TableContainer>
  );
};

export default InvoiceList;
